"use client";

import { useReportWebVitals } from "next/web-vitals";
import posthog from "posthog-js";
import { getAnalyticsConsent } from "@/lib/analytics";
import { isPostHogInitialized } from "@/lib/posthog-init";

/**
 * Streams Core Web Vitals (LCP, INP, CLS, FCP, TTFB) from real users into
 * PostHog and Google Analytics so we can monitor field data without waiting
 * on CrUX. Firing client-side is the only way to capture these metrics
 * because they are measured in the browser.
 *
 * Thresholds match web.dev's "good/needs-improvement/poor" buckets so the
 * bucketed events are easy to chart in PostHog insights.
 */
function rating(name: string, value: number): "good" | "needs-improvement" | "poor" {
  switch (name) {
    case "LCP":
      return value <= 2500 ? "good" : value <= 4000 ? "needs-improvement" : "poor";
    case "INP":
      return value <= 200 ? "good" : value <= 500 ? "needs-improvement" : "poor";
    case "CLS":
      return value <= 0.1 ? "good" : value <= 0.25 ? "needs-improvement" : "poor";
    case "FCP":
      return value <= 1800 ? "good" : value <= 3000 ? "needs-improvement" : "poor";
    case "TTFB":
      return value <= 800 ? "good" : value <= 1800 ? "needs-improvement" : "poor";
    default:
      return "good";
  }
}

export function WebVitalsReporter(): null {
  useReportWebVitals((metric) => {
    const { name, value, id, delta, navigationType } = metric;
    const bucket = rating(name, value);

    // Round to sensible precision to avoid flooding storage with noisy decimals.
    const rounded = name === "CLS" ? Math.round(value * 1000) / 1000 : Math.round(value);

    if (typeof window !== "undefined" && getAnalyticsConsent() === "accepted") {
      try {
        if (isPostHogInitialized()) {
          posthog.capture("$web_vitals", {
          metric_name: name,
          metric_value: rounded,
          metric_delta: delta,
          metric_id: id,
          metric_rating: bucket,
          navigation_type: navigationType,
          page_path: window.location.pathname,
        });
        }
      } catch {
        // PostHog may not be loaded (e.g. consent denied) – silently ignore.
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", name, {
          value: rounded,
          metric_id: id,
          metric_delta: delta,
          metric_rating: bucket,
          non_interaction: true,
        });
      }
    }
  });

  return null;
}
